import React, {useCallback, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router';
import type {NoProps} from 'types';
import type {BulkUpdateSummary} from 'api/types';
import {useLocalization} from 'localization';
import {useGetBulkUpdatesQuery} from 'services/bulkUpdates/bulkUpdatesApi';
import {Scene} from 'components';
import {SceneTitle} from 'components/scene/SceneTitle';
import {Table} from 'schema';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';
import {bulkUpdateSummaryTableConfig} from 'schemas';
import {bulkUpdateSummarySchema} from 'schemas/schemas';
import {useMapThesaurusValues} from './hooks/useMapThesaurusValues';

export const BulkUpdateListSceneCore: React.FC<NoProps> = () => {
  const {t} = useLocalization();
  const navigate = useNavigate();
  const title = t('appbar.bulkUpdate');

  const {paginationModel, resetPaginationModel} = useColumnConfigState(
    bulkUpdateSummarySchema.name,
  );

  const {data, isFetching} = useGetBulkUpdatesQuery({
    page: paginationModel.page,
    size: paginationModel.pageSize,
  });

  useEffect(() => {
    resetPaginationModel();
  }, [resetPaginationModel]);

  const handleOnClick = useCallback(
    (row: BulkUpdateSummary) => {
      navigate(`/bulkUpdate/details/${row.id}`);
    },
    [navigate],
  );

  const genreAndFormMapper = useMapThesaurusValues('genreandform');
  const subjectsMapper = useMapThesaurusValues('bokbasen');
  const themaMapper = useMapThesaurusValues('thema');
  const neoMapper = useMapThesaurusValues('neo');

  const readableValues = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.items.map(item => {
      if (item.bulkUpdateField === 'genreAndForm') {
        return {
          ...item,
          currentValues: genreAndFormMapper(item.currentValues as string[]),
          newValues: genreAndFormMapper(item.newValues as string[]),
        };
      }
      if (item.bulkUpdateField === 'subjects') {
        return {
          ...item,
          currentValues: subjectsMapper(item.currentValues as string[]),
          newValues: subjectsMapper(item.newValues as string[]),
        };
      }
      if (item.bulkUpdateField === 'themes') {
        return {
          ...item,
          currentValues: themaMapper(item.currentValues as string[]),
          newValues: themaMapper(item.newValues as string[]),
        };
      }
      if (item.bulkUpdateField === 'norwegianSubjects') {
        return {
          ...item,
          currentValues: neoMapper(item.currentValues as string[]),
          newValues: neoMapper(item.newValues as string[]),
        };
      }

      return item;
    });
  }, [data, genreAndFormMapper, neoMapper, subjectsMapper, themaMapper]);

  return (
    <Scene browserTitle={title}>
      <Scene.Header>
        <SceneTitle title={title} />
      </Scene.Header>
      <Scene.Content>
        <Table<BulkUpdateSummary>
          schema={bulkUpdateSummarySchema}
          configuration={bulkUpdateSummaryTableConfig}
          data={readableValues ?? []}
          rowCount={data?.elements ?? 0}
          header={title}
          loading={isFetching}
          onRowClick={handleOnClick}
        />
      </Scene.Content>
    </Scene>
  );
};
